import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Shield, Eye, EyeOff, LogOut, Save, Users, Flame, Target, Activity, Megaphone, AlertCircle, LayoutDashboard, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import AdsManager from '../components/secretadmin/AdsManager';
import SecretDashboard from '../components/secretadmin/SecretDashboard';
import EntityManager from '../components/secretadmin/EntityManager';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('http://', 'https://');
const TOKEN_KEY = 'loveall:secret_admin_token';

const apiCall = async (path, opts = {}) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  if (token) headers['X-Secret-Admin-Token'] = token;
  const res = await fetch(`${BACKEND_URL}${path}`, { ...opts, headers });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

function GateForm({ onAuthed }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall('/api/admin-secret/login', {
        method: 'POST',
        body: JSON.stringify({
          // Username is case-sensitive on the backend and is documented as
          // lowercase. Mobile keyboards / browser autofill love to capitalize
          // the first character — normalize to lowercase to match the env var.
          username: username.trim().toLowerCase(),
          password: password,
          token: token.trim().toUpperCase().replace(/\s/g, ''),
        }),
      });
      // Save token to sessionStorage for subsequent API calls
      sessionStorage.setItem(TOKEN_KEY, token.trim().toUpperCase().replace(/\s/g, ''));
      sessionStorage.setItem(TOKEN_KEY + ':user', username.trim().toLowerCase());
      onAuthed();
    } catch (err) {
      toast.error(err.message?.includes('401') || err.message?.toLowerCase().includes('denied')
        ? 'Wrong username, password or token. Please check all three.'
        : err.message || 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 safe-top safe-bottom" data-testid="secret-gate">
      <Card className="w-full max-w-sm border-2 border-amber-500/30">
        <CardHeader className="text-center">
          <Shield className="h-10 w-10 text-amber-500 mx-auto mb-2" />
          <CardTitle className="font-heading uppercase tracking-tight text-base">Restricted Access</CardTitle>
          <p className="text-xs text-muted-foreground">Internal Dashboard — Authorized Personnel Only</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="su" className="text-xs">Username</Label>
              <Input
                id="su"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                required
                data-testid="secret-username"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Case-sensitive (all lowercase)</p>
            </div>
            <div>
              <Label htmlFor="sp" className="text-xs">Password</Label>
              <Input
                id="sp"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                required
                data-testid="secret-password"
              />
            </div>
            <div>
              <Label htmlFor="st" className="text-xs">Admin Access Token</Label>
              <div className="relative">
                <Input
                  id="st"
                  type={showToken ? 'text' : 'password'}
                  value={token}
                  onChange={(e) => setToken(e.target.value.toUpperCase().replace(/\s/g, ''))}
                  maxLength={12}
                  placeholder="e.g. LV7K3M9P2"
                  className="font-mono uppercase tracking-wider pr-10"
                  autoComplete="off"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  data-testid="secret-token"
                />
                <button
                  type="button"
                  onClick={() => setShowToken(s => !s)}
                  className="absolute right-2 top-2.5 text-muted-foreground"
                  data-testid="secret-token-toggle"
                >
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="secret-login-btn">
              {loading ? 'Verifying...' : 'Enter Dashboard'}
            </Button>
          </form>
          <p className="text-[10px] text-muted-foreground text-center mt-4">
            ⚠️ Failed attempts are logged.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [metrics, setMetrics] = useState(null);
  const [adConfig, setAdConfig] = useState({
    banner_style: 'image_loop',
    sticky_text: '',
    interval_image_url: '',
    interval_text: '',
    click_through_url: '',
    ads_enabled: true,
  });
  const [savingAd, setSavingAd] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [error, setError] = useState(null);

  const loadAll = async () => {
    try {
      const [m, ad] = await Promise.all([
        apiCall('/api/admin-secret/metrics'),
        apiCall('/api/admin-secret/ad-config'),
      ]);
      setMetrics(m);
      setAdConfig({ ...adConfig, ...ad });
      setError(null);
    } catch (e) {
      setError(e.message);
      if (e.message.includes('401') || e.message.toLowerCase().includes('unauthor')) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
      }
    } finally {
      setLoadingMetrics(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const saveAd = async () => {
    setSavingAd(true);
    try {
      await apiCall('/api/admin-secret/ad-config', {
        method: 'PUT',
        body: JSON.stringify(adConfig),
      });
      toast.success('Ad settings saved');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingAd(false);
    }
  };

  const username = sessionStorage.getItem(TOKEN_KEY + ':user') || 'admin';

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom pb-12" data-testid="secret-dashboard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-amber-500/10 border-b-2 border-amber-500/30 backdrop-blur px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-amber-500" />
            <h1 className="font-heading text-base sm:text-lg font-extrabold uppercase tracking-tight">LoveAll Stealth Admin</h1>
            <Badge variant="outline" className="text-[10px] uppercase">{username}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="secret-logout">
            <LogOut className="h-4 w-4 mr-1" /> Exit
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {error && (
          <Card className="border-red-500/40 bg-red-500/5">
            <CardContent className="p-4 text-sm text-red-500 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> {error}
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="metrics" data-testid="secret-admin-tabs">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="metrics" data-testid="tab-metrics"><Activity className="h-3.5 w-3.5 mr-1" /> Metrics</TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads"><Megaphone className="h-3.5 w-3.5 mr-1" /> Ads</TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="h-3.5 w-3.5 mr-1" /> Dashboard</TabsTrigger>
            <TabsTrigger value="manage" data-testid="tab-manage"><Trophy className="h-3.5 w-3.5 mr-1" /> Manage</TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="space-y-6 pt-4">

        {/* Metrics */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-bold">[2] Silent Signups & Traction Metrics</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              icon={<Users className="h-4 w-4" />}
              label="Shadow Profiles"
              value={loadingMetrics ? '—' : metrics?.shadow_profiles?.toLocaleString() || 0}
              testid="metric-shadow"
            />
            <MetricCard
              icon={<Flame className="h-4 w-4" />}
              label="Active Streaks (3+)"
              value={loadingMetrics ? '—' : metrics?.active_streaks?.toLocaleString() || 0}
              testid="metric-streaks"
            />
            <MetricCard
              icon={<Target className="h-4 w-4" />}
              label="3x15 Matches Today"
              value={loadingMetrics ? '—' : metrics?.matches_3x15_today?.toLocaleString() || 0}
              testid="metric-3x15-today"
            />
            <MetricCard
              icon={<Activity className="h-4 w-4" />}
              label="Total Matches"
              value={loadingMetrics ? '—' : metrics?.total_matches?.toLocaleString() || 0}
              testid="metric-total-matches"
            />
            <MetricCard
              icon={<Shield className="h-4 w-4" />}
              label="Unique Devices"
              value={loadingMetrics ? '—' : metrics?.unique_devices?.toLocaleString() || 0}
              testid="metric-unique-devices"
            />
            <MetricCard
              icon={<Users className="h-4 w-4" />}
              label="Active (24h)"
              value={loadingMetrics ? '—' : metrics?.active_24h?.toLocaleString() || 0}
              testid="metric-active-24h"
            />
            <MetricCard
              icon={<Megaphone className="h-4 w-4" />}
              label="With Geo"
              value={loadingMetrics ? '—' : metrics?.with_geo?.toLocaleString() || 0}
              testid="metric-with-geo"
            />
          </div>
        </section>
          </TabsContent>

          <TabsContent value="ads" className="space-y-6 pt-4">
            <AdsManager apiCall={apiCall} />

        {/* Ad Config (interval-break) */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-bold">Interval-Break Default (Drinks Break Card)</h2>
          <Card>
            <CardContent className="p-5 space-y-4">
              {/* Global ads kill-switch — hides every ad slot without deleting campaigns */}
              <div className="flex items-center justify-between rounded-lg border border-border p-3" data-testid="ads-enabled-row">
                <div>
                  <p className="text-sm font-semibold">Show ads across the app</p>
                  <p className="text-xs text-muted-foreground">Turn off to hide every ad slot (banners + drinks-break card). Campaigns are kept, just not shown.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={adConfig.ads_enabled !== false}
                  onClick={() => setAdConfig(a => ({ ...a, ads_enabled: !(a.ads_enabled !== false) }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${adConfig.ads_enabled !== false ? 'bg-emerald-500' : 'bg-muted'}`}
                  data-testid="ads-enabled-toggle"
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${adConfig.ads_enabled !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Active Banner Style</Label>
                  <Select value={adConfig.banner_style} onValueChange={(v) => setAdConfig(a => ({ ...a, banner_style: v }))}>
                    <SelectTrigger data-testid="ad-banner-style"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image_loop">Image Loop</SelectItem>
                      <SelectItem value="text_only">Text Only</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Click-Thru URL</Label>
                  <Input
                    placeholder="https://courtproshop.com/buy"
                    value={adConfig.click_through_url}
                    onChange={(e) => setAdConfig(a => ({ ...a, click_through_url: e.target.value }))}
                    data-testid="ad-click-url"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Sticky Bottom Strip Text</Label>
                <Input
                  placeholder='Advertise with us: biz@loveall.app'
                  value={adConfig.sticky_text}
                  onChange={(e) => setAdConfig(a => ({ ...a, sticky_text: e.target.value }))}
                  data-testid="ad-sticky-text"
                />
              </div>
              <div>
                <Label className="text-xs">Interval Display Image URL (shown at 8-point break)</Label>
                <Input
                  placeholder="https://i.imgur.com/gatorade.png"
                  value={adConfig.interval_image_url}
                  onChange={(e) => setAdConfig(a => ({ ...a, interval_image_url: e.target.value }))}
                  data-testid="ad-interval-image"
                />
              </div>
              <div>
                <Label className="text-xs">Interval Tagline</Label>
                <Input
                  placeholder="Thirsty? Grab a Gatorade at the pro-shop!"
                  value={adConfig.interval_text}
                  onChange={(e) => setAdConfig(a => ({ ...a, interval_text: e.target.value }))}
                  data-testid="ad-interval-text"
                />
              </div>

              {/* Preview */}
              <div className="rounded-lg border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/10 p-4 space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500 flex items-center gap-1"><Megaphone className="h-3 w-3" /> Live Preview</p>
                {adConfig.interval_image_url ? (
                  <img src={adConfig.interval_image_url} alt="preview" className="w-full h-28 object-cover rounded" />
                ) : (
                  <div className="h-28 flex items-center justify-center text-sm text-muted-foreground italic">No image set</div>
                )}
                {adConfig.interval_text && <p className="text-sm text-center font-medium">{adConfig.interval_text}</p>}
              </div>

              <Button onClick={saveAd} disabled={savingAd} className="w-full" data-testid="ad-save-btn">
                <Save className="h-4 w-4 mr-1" /> {savingAd ? 'Saving...' : 'Save Ad Settings'}
              </Button>
            </CardContent>
          </Card>
        </section>
          </TabsContent>

          <TabsContent value="dashboard" className="pt-4">
            <SecretDashboard apiCall={apiCall} />
          </TabsContent>

          <TabsContent value="manage" className="pt-4">
            <EntityManager apiCall={apiCall} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function MetricCard({ icon, label, value, testid }) {
  return (
    <Card>
      <CardContent className="p-4 space-y-1.5">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          {icon}
          <p className="text-[10px] uppercase tracking-widest">{label}</p>
        </div>
        <p className="font-heading text-3xl font-extrabold tabular-nums" data-testid={testid}>{value}</p>
      </CardContent>
    </Card>
  );
}

export default function SecretAdmin() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(TOKEN_KEY));

  if (!authed) return <GateForm onAuthed={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); }} />;
}
